/**
 * Dropdown selector for switching between accounts and teams.
 * Renders a vertical stacked layout with "Account" and "Team" labels.
 */
import { useEffect, useMemo, useState } from "react";

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useSwitchScope } from "@/hooks/useSwitchScope";
import { useAuthStore } from "@/stores/auth";

/**
 * Hierarchical Account/Team selector
 *
 * - Account dropdown always visible
 * - Team dropdown animates in when account has teams
 * - Auto-selects single options (account or team)
 */
export function AccountTeamSelector() {
	const { teams, accounts, activeTeam } = useAuthStore();
	const switchScope = useSwitchScope();

	// Track selected account (can differ from activeTeam.account during selection)
	const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
		activeTeam?.account.id ?? null
	);

	// Sync selectedAccountId when activeTeam changes externally
	useEffect(() => {
		if (activeTeam) {
			setSelectedAccountId(activeTeam.account.id);
		}
	}, [activeTeam]);

	// Auto-select single account
	useEffect(() => {
		if (accounts.length === 1 && !selectedAccountId && accounts[0]) {
			const account = accounts[0];
			setSelectedAccountId(account.id);

			// Also auto-select first team for this account if available
			const teamsForAccount = teams.filter((t) => t.account.id === account.id);
			if (teamsForAccount.length >= 1 && teamsForAccount[0] && teamsForAccount[0].id !== activeTeam?.id) {
				void switchScope(teamsForAccount[0]);
			}
		}
	}, [accounts, selectedAccountId, teams, activeTeam, switchScope]);

	// Get teams for selected account
	const accountTeams = useMemo(() => {
		if (!selectedAccountId) return [];
		return teams.filter((t) => t.account.id === selectedAccountId);
	}, [teams, selectedAccountId]);

	// Show team selector if user has access to any teams
	const showTeamSelector = accountTeams.length >= 1;

	/**
	 * Handles account selection and auto-selects the first team
	 * @param accountId - the selected account ID
	 */
	const handleAccountChange = (accountId: string) => {
		setSelectedAccountId(accountId);

		const teamsForAccount = teams.filter((t) => t.account.id === accountId);

		// Auto-select team if single team account, or first team for multi-team.
		// Switching scope re-issues the session and invalidates cached data.
		if (teamsForAccount.length >= 1 && teamsForAccount[0]) {
			void switchScope(teamsForAccount[0]);
		}
	};

	/**
	 * Handles team selection
	 * @param teamId - the selected team ID
	 */
	const handleTeamChange = (teamId: string) => {
		const team = teams.find((t) => t.id === teamId);
		if (team) {
			void switchScope(team);
		}
	};

	// Empty state
	if (accounts.length === 0) {
		return (
			<div className="text-sm text-sidebar-muted">No accounts available</div>
		);
	}

	return (
		<div>
			{/* Account Selector */}
			<div className="space-y-1">
				<label className="text-xs font-medium uppercase tracking-wider text-sidebar-muted">
					Account
				</label>
				<Select
					value={selectedAccountId ?? ""}
					onValueChange={handleAccountChange}
				>
					<SelectTrigger className="w-full border-sidebar-border bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent ">
						<SelectValue placeholder="Select account" />
					</SelectTrigger>
					<SelectContent>
						{accounts.map((account) => (
							<SelectItem key={account.id} value={account.id}>
								{account.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* Team Selector - only shown when multiple teams exist */}
			{showTeamSelector && (
				<div className="mt-3 space-y-1">
					<label className="text-xs font-medium uppercase tracking-wider text-sidebar-muted">
						Team
					</label>
					<Select
						value={activeTeam?.id ?? ""}
						onValueChange={handleTeamChange}
					>
						<SelectTrigger className="w-full border-sidebar-border bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent">
							<SelectValue placeholder="Select team" />
						</SelectTrigger>
						<SelectContent>
							{accountTeams.map((team) => (
								<SelectItem key={team.id} value={team.id}>
									{team.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}
		</div>
	);
}
